import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type LogRow = {
  id: string;
  message_id: string | null;
  template_name: string | null;
  recipient_email: string | null;
  status: string | null;
  error_message: string | null;
  created_at: string;
};

type SuppressionRow = {
  email: string;
  reason: string | null;
  created_at: string;
};

const RANGES = [
  { key: "24h", label: "Last 24h", hours: 24 },
  { key: "7d", label: "Last 7 days", hours: 24 * 7 },
  { key: "30d", label: "Last 30 days", hours: 24 * 30 },
];

const statusBadge = (status: string | null) => {
  if (status === "sent") return <Badge className="bg-green-600 text-white hover:bg-green-600">sent</Badge>;
  if (status === "dlq" || status === "failed") return <Badge variant="destructive">{status}</Badge>;
  if (status === "suppressed") return <Badge variant="secondary">suppressed</Badge>;
  if (status === "bounced" || status === "complained") return <Badge variant="destructive">{status}</Badge>;
  return <Badge variant="outline">{status ?? "unknown"}</Badge>;
};

export const EmailDeliveryAuditPanel = () => {
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [suppressed, setSuppressed] = useState<SuppressionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState("7d");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");

  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[1];

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - range.hours * 60 * 60 * 1000).toISOString();
    const [logsRes, suppRes] = await Promise.all([
      supabase
        .from("email_send_log")
        .select("id, message_id, template_name, recipient_email, status, error_message, created_at")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("suppressed_emails")
        .select("email, reason, created_at")
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    if (logsRes.error) toast.error(logsRes.error.message);
    if (suppRes.error) toast.error(suppRes.error.message);
    setLogs((logsRes.data ?? []) as LogRow[]);
    setSuppressed((suppRes.data ?? []) as SuppressionRow[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  // Dedupe by message_id, keeping latest (rows are already DESC ordered)
  const deduped = useMemo(() => {
    const seen = new Set<string>();
    const out: LogRow[] = [];
    for (const row of logs) {
      const key = row.message_id ?? row.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
    return out;
  }, [logs]);

  const templates = useMemo(() => {
    const set = new Set<string>();
    deduped.forEach((r) => r.template_name && set.add(r.template_name));
    return Array.from(set).sort();
  }, [deduped]);

  const filtered = useMemo(() => {
    return deduped.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (templateFilter !== "all" && r.template_name !== templateFilter) return false;
      return true;
    });
  }, [deduped, statusFilter, templateFilter]);

  const stats = useMemo(() => {
    const counts = { total: deduped.length, sent: 0, failed: 0, suppressed: 0 };
    deduped.forEach((r) => {
      if (r.status === "sent") counts.sent += 1;
      else if (r.status === "dlq" || r.status === "failed" || r.status === "bounced") counts.failed += 1;
      else if (r.status === "suppressed") counts.suppressed += 1;
    });
    return counts;
  }, [deduped]);

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Delivery audit</CardTitle>
            <p className="text-sm text-muted-foreground">
              Read-only view of recent sends from the email log, deduped by message id, plus the suppression list.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="dlq">DLQ / failed</SelectItem>
              <SelectItem value="suppressed">Suppressed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={templateFilter} onValueChange={setTemplateFilter}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              {templates.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <StatCard label="Unique sends" value={stats.total} />
          <StatCard label="Sent" value={stats.sent} tone="success" />
          <StatCard label="Failed / DLQ" value={stats.failed} tone="danger" />
          <StatCard label="Suppressed" value={stats.suppressed} tone="muted" />
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Template</th>
                <th className="px-3 py-2">Recipient</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No sends in this window.</td></tr>
              ) : (
                filtered.slice(0, 100).map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs">{r.template_name ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">{r.recipient_email ?? "—"}</td>
                    <td className="px-3 py-2">{statusBadge(r.status)}</td>
                    <td className="px-3 py-2 text-xs text-destructive">{r.error_message ?? ""}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium">Suppression list (latest 100)</h3>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Suppressed at</th>
                </tr>
              </thead>
              <tbody>
                {suppressed.length === 0 ? (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No suppressed addresses.</td></tr>
                ) : (
                  suppressed.map((s) => (
                    <tr key={s.email} className="border-t border-border">
                      <td className="px-3 py-2 text-xs">{s.email}</td>
                      <td className="px-3 py-2 text-xs">{s.reason ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ label, value, tone }: { label: string; value: number; tone?: "success" | "danger" | "muted" }) => {
  const toneClass =
    tone === "success" ? "text-green-600" :
    tone === "danger" ? "text-destructive" :
    tone === "muted" ? "text-muted-foreground" : "text-foreground";
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};
